import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../components/Sidebar';
import DashboardHeader from '../components/DashboardHeader';
import { apiRequest, API_BASE_URL } from '../api';
import CreateIntegrationModal from '../components/CreateIntegrationModal';
import EditIntegrationBasicModal from '../components/EditIntegrationBasicModal';
import EditIntegrationSyncModal from '../components/EditIntegrationSyncModal';
import EditIntegrationConfigModal from '../components/EditIntegrationConfigModal';

// SyncHistoryItem Component
const SyncHistoryItem = ({ log, formatDate }) => {
    const [isExpanded, setIsExpanded] = useState(false);
    
    const getStatusIcon = (status) => {
        switch (status?.toLowerCase()) {
            case 'success':
                return 'fas fa-check-circle text-success';
            case 'failed':
                return 'fas fa-exclamation-triangle text-danger';
            case 'running':
                return 'fas fa-sync fa-spin text-primary';
            default:
                return 'fas fa-info-circle text-info';
        }
    };

    const getStatusColor = (status) => {
        switch (status?.toLowerCase()) {
            case 'success':
                return 'success';
            case 'failed':
                return 'danger';
            case 'running':
                return 'primary';
            default:
                return 'info';
        }
    };

    const getOperationIcon = (operationType) => {
        switch (operationType?.toLowerCase()) {
            case 'manual':
                return <i className="fas fa-hand-paper" style={{ fontSize: '12px', color: '#007bff' }} title="Manual Sync"></i>;
            case 'scheduled':
                return <i className="fas fa-clock" style={{ fontSize: '12px', color: '#6c757d' }} title="Scheduled Sync"></i>;
            case 'test':
                return <i className="fas fa-vial" style={{ fontSize: '12px', color: '#ffc107' }} title="Test Sync"></i>;
            default:
                return <i className="fas fa-sync" style={{ fontSize: '12px', color: '#6c757d' }} title="Sync Operation"></i>;
        }
    };

    const getOperationDisplayName = (operationType, status) => {
        // Handle specific operation types from backend
        switch (operationType?.toLowerCase()) {
            case 'manual':
                return 'Manual Sync';
            case 'scheduled':
                return 'Scheduled Sync';
            case 'test':
                return 'Test Sync';
            default:
                // If it's running, show that regardless of operation type
                if (status?.toLowerCase() === 'running') {
                    return 'Sync in Progress';
                }
                // Fallback to operation type or default
                return operationType || 'Sync Operation';
        }
    };

    return (
        <div className="sync-history-item">
            <div 
                className="sync-history-summary"
                onClick={() => setIsExpanded(!isExpanded)}
                style={{
                    cursor: 'pointer',
                    padding: '12px 16px',
                    border: '1px solid #e0e0e0',
                    borderRadius: '6px',
                    marginBottom: '8px',
                    backgroundColor: '#fff',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    transition: 'all 0.2s ease'
                }}
            >
                <div className="sync-summary-left" style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <i className={getStatusIcon(log.status)} style={{ fontSize: '16px' }}></i>
                    <div className="sync-basic-info">
                        <div style={{ fontWeight: '600', fontSize: '14px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                            {getOperationIcon(log.operation_type)}
                            {getOperationDisplayName(log.operation_type, log.status)}
                        </div>
                        <div style={{ fontSize: '12px', color: '#666' }}>
                            {formatDate(log.started_at)}
                        </div>
                    </div>
                </div>
                
                <div className="sync-summary-right" style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                    <div className="sync-quick-stats" style={{ display: 'flex', gap: '12px', fontSize: '12px' }}>
                        <span style={{ color: '#666' }}>
                            <i className="fas fa-database" style={{ marginRight: '4px' }}></i>
                            {log.records_processed || 0}
                        </span>
                        <span style={{ color: '#28a745' }}>
                            <i className="fas fa-check" style={{ marginRight: '4px' }}></i>
                            {log.records_successful || 0}
                        </span>
                        {(log.records_failed || 0) > 0 && (
                            <span style={{ color: '#dc3545' }}>
                                <i className="fas fa-times" style={{ marginRight: '4px' }}></i>
                                {log.records_failed || 0}
                            </span>
                        )}
                        {log.duration_seconds && (
                            <span style={{ color: '#666' }}>
                                <i className="fas fa-clock" style={{ marginRight: '4px' }}></i>
                                {log.duration_seconds.toFixed(1)}s
                            </span>
                        )}
                    </div>
                    
                    <div className="sync-expand-icon" style={{ 
                        transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                        transition: 'transform 0.2s ease'
                    }}>
                        <i className="fas fa-chevron-down" style={{ fontSize: '12px', color: '#666' }}></i>
                    </div>
                </div>
            </div>
            
            {isExpanded && (
                <div className="sync-history-details" style={{
                    padding: '16px',
                    border: '1px solid #e0e0e0',
                    borderTop: 'none',
                    borderRadius: '0 0 6px 6px',
                    backgroundColor: '#f8f9fa',
                    marginTop: '-8px',
                    marginBottom: '8px'
                }}>
                    <div className="sync-details-grid" style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                        gap: '16px',
                        marginBottom: '16px'
                    }}>
                        <div className="detail-item">
                            <label style={{ fontSize: '12px', color: '#666', fontWeight: '600' }}>Status</label>
                            <div style={{ fontSize: '14px', fontWeight: '500' }}>
                                <span className={`badge badge-${getStatusColor(log.status)}`} style={{
                                    padding: '4px 8px',
                                    borderRadius: '4px',
                                    fontSize: '11px',
                                    textTransform: 'uppercase'
                                }}>
                                    {log.status}
                                </span>
                            </div>
                        </div>
                        
                        <div className="detail-item">
                            <label style={{ fontSize: '12px', color: '#666', fontWeight: '600' }}>Started</label>
                            <div style={{ fontSize: '14px' }}>{formatDate(log.started_at)}</div>
                        </div>
                        
                        {log.completed_at && (
                            <div className="detail-item">
                                <label style={{ fontSize: '12px', color: '#666', fontWeight: '600' }}>Completed</label>
                                <div style={{ fontSize: '14px' }}>{formatDate(log.completed_at)}</div>
                            </div>
                        )}
                        
                        {log.duration_seconds && (
                            <div className="detail-item">
                                <label style={{ fontSize: '12px', color: '#666', fontWeight: '600' }}>Duration</label>
                                <div style={{ fontSize: '14px' }}>{log.duration_seconds.toFixed(2)} seconds</div>
                            </div>
                        )}
                    </div>
                    
                    <div className="sync-metrics-detailed" style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))',
                        gap: '12px',
                        marginBottom: '16px'
                    }}>
                        <div className="metric-card" style={{
                            padding: '12px',
                            backgroundColor: '#fff',
                            borderRadius: '4px',
                            border: '1px solid #e0e0e0',
                            textAlign: 'center'
                        }}>
                            <div style={{ fontSize: '20px', fontWeight: 'bold', color: '#666' }}>
                                {log.records_processed || 0}
                            </div>
                            <div style={{ fontSize: '11px', color: '#666', textTransform: 'uppercase' }}>
                                Processed
                            </div>
                        </div>
                        
                        <div className="metric-card" style={{
                            padding: '12px',
                            backgroundColor: '#fff',
                            borderRadius: '4px',
                            border: '1px solid #e0e0e0',
                            textAlign: 'center'
                        }}>
                            <div style={{ fontSize: '20px', fontWeight: 'bold', color: '#28a745' }}>
                                {log.records_successful || 0}
                            </div>
                            <div style={{ fontSize: '11px', color: '#666', textTransform: 'uppercase' }}>
                                Successful
                            </div>
                        </div>
                        
                        <div className="metric-card" style={{
                            padding: '12px',
                            backgroundColor: '#fff',
                            borderRadius: '4px',
                            border: '1px solid #e0e0e0',
                            textAlign: 'center'
                        }}>
                            <div style={{ fontSize: '20px', fontWeight: 'bold', color: '#dc3545' }}>
                                {log.records_failed || 0}
                            </div>
                            <div style={{ fontSize: '11px', color: '#666', textTransform: 'uppercase' }}>
                                Failed
                            </div>
                        </div>
                    </div>
                    
                    {log.error_message && (
                        <div className="sync-error" style={{
                            padding: '12px',
                            backgroundColor: '#f8d7da',
                            border: '1px solid #f5c6cb',
                            borderRadius: '4px',
                            marginBottom: '16px'
                        }}>
                            <div style={{ fontWeight: '600', color: '#721c24', marginBottom: '4px' }}>
                                <i className="fas fa-exclamation-triangle" style={{ marginRight: '6px' }}></i>
                                Error Message
                            </div>
                            <div style={{ fontSize: '13px', color: '#721c24' }}>
                                {log.error_message}
                            </div>
                        </div>
                    )}
                    
                    {log.failed_submissions && log.failed_submissions.length > 0 && (
                        <div className="failed-submissions" style={{ marginTop: '16px' }}>
                            <div style={{ fontWeight: '600', marginBottom: '8px', fontSize: '14px' }}>
                                <i className="fas fa-times-circle" style={{ marginRight: '6px', color: '#dc3545' }}></i>
                                Failed Submissions ({log.failed_submissions.length})
                            </div>
                            <div className="failed-submissions-list" style={{
                                maxHeight: '200px',
                                overflowY: 'auto',
                                border: '1px solid #e0e0e0',
                                borderRadius: '4px'
                            }}>
                                {log.failed_submissions.map((submission, subIndex) => (
                                    <div key={subIndex} className="failed-submission-item" style={{
                                        padding: '8px 12px',
                                        borderBottom: subIndex < log.failed_submissions.length - 1 ? '1px solid #e0e0e0' : 'none',
                                        backgroundColor: '#fff',
                                        fontSize: '12px'
                                    }}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
                                            <span style={{ fontWeight: '600', color: '#666' }}>
                                                ID: {submission.submission_id}
                                            </span>
                                            <span style={{ color: '#666', fontSize: '11px' }}>
                                                {formatDate(submission.synced_at)}
                                            </span>
                                        </div>
                                        <div style={{ color: '#dc3545', fontSize: '11px' }}>
                                            {submission.error_message}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

const UnifiedIntegrationManagement = () => {
    const [integrations, setIntegrations] = useState([]);
    const [templates, setTemplates] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [showEditBasicModal, setShowEditBasicModal] = useState(false);
    const [showEditSyncModal, setShowEditSyncModal] = useState(false);
    const [showEditConfigModal, setShowEditConfigModal] = useState(false);
    const [showTestModal, setShowTestModal] = useState(false);
    const [showSyncModal, setShowSyncModal] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [selectedIntegration, setSelectedIntegration] = useState(null);
    const [selectedTemplate, setSelectedTemplate] = useState(null);
    const apiUrl = process.env.REACT_APP_API_BASE_URL;
    const [activeTab, setActiveTab] = useState('integrations'); // 'integrations' or 'sync'
    const [formData, setFormData] = useState({
        name: '',
        description: '',
        template_id: '',
        configuration: {},
        is_active: true,
        sync_enabled: false,
        sync_settings: {
            mode: 'single',
            batch_size: 50,
            interval_minutes: 15,
            records_per_interval: 15,
            rate_limit: {
                requests_per_minute: 60
            }
        }
    });
    const [testResults, setTestResults] = useState(null);
    const [syncResults, setSyncResults] = useState(null);
    const [syncJobs, setSyncJobs] = useState([]);
    const [errors, setErrors] = useState({});
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [integrationToDelete, setIntegrationToDelete] = useState(null);
    const [deleteConfirmText, setDeleteConfirmText] = useState("");
    const [showConfigModal, setShowConfigModal] = useState(false);
    const [syncHistory, setSyncHistory] = useState([]);
    const [showScheduleModal, setShowScheduleModal] = useState(false);
    const [syncLoading, setSyncLoading] = useState({}); // Track loading state for each integration
    const [scheduleConfig, setScheduleConfig] = useState({
        type: 'interval',
        interval_minutes: 15,
        hour: 2,
        minute: 0,
        timezone: 'UTC',
        enabled: true
    });
    const [selectedIntegrationForHistory, setSelectedIntegrationForHistory] = useState(null);
    const navigate = useNavigate();

    // Get user role and universityId from localStorage
    const userRole = localStorage.getItem('role');
    const uniAdminUniversityId = localStorage.getItem('universityId');

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem('token');
            const [integrationsResponse, templatesResponse] = await Promise.all([
                apiRequest(`${API_BASE_URL}/university-integrations/`, 'GET', null, token),
                apiRequest(`${API_BASE_URL}/integration-templates/`, 'GET', null, token)
            ]);

            console.log('Fetched integrations response:', integrationsResponse);
            console.log('First integration sync_schedule:', integrationsResponse.integrations?.[0]?.sync_schedule);
            console.log('Fetched templates response:', templatesResponse);
            setIntegrations(integrationsResponse.integrations || []);
            setTemplates(templatesResponse.templates || []);
        } catch (error) {
            console.error('Error fetching data:', error);
        } finally {
            setLoading(false);
        }
    };

    const loadSyncJobs = async () => {
        try {
            const universityId = userRole === 'uni_admin' ? uniAdminUniversityId : selectedIntegration?.university_id;
            if (universityId) {
                const token = localStorage.getItem('token');
                const response = await apiRequest(`${API_BASE_URL}/crm-sync/jobs/${universityId}`, 'GET', null, token);
                if (response.success) {
                    setSyncJobs(response.data || []);
                }
            }
        } catch (error) {
            console.error('Error loading sync jobs:', error);
        }
    };

    const fetchSyncHistory = async (integrationId) => {
        try {
            const token = localStorage.getItem('token');
            console.log('Fetching sync history for integration:', integrationId);
            const response = await apiRequest(`${API_BASE_URL}/sync-management/integrations/${integrationId}/history`, 'GET', null, token);
            console.log('Sync history response:', response);
            setSyncHistory(response || []);
        } catch (err) {
            console.error('Error fetching sync history:', err);
            setSyncHistory([]);
        }
    };

    const updateIntegrationSyncSettings = async (integrationId, newSettings) => {
        try {
            const token = localStorage.getItem('token');
            const response = await apiRequest(`${API_BASE_URL}/university-integrations/${integrationId}`, 'PUT', {
                sync_enabled: newSettings.enabled,
                configuration: {
                    sync_settings: {
                        schedule_type: newSettings.type,
                        interval_minutes: newSettings.interval_minutes,
                        hour: newSettings.hour,
                        minute: newSettings.minute,
                        timezone: newSettings.timezone,
                        batch_size: newSettings.batch_size || 50,
                        mode: newSettings.mode || 'single'
                    }
                }
            }, token);
            
            if (response) {
                alert('Sync settings updated successfully!');
                fetchData(); // Refresh the list
                setShowScheduleModal(false);
            }
        } catch (err) {
            alert('Failed to update sync settings');
            console.error('Error updating sync settings:', err);
        }
    };

    const updateIntegrationEnabledStatus = async (integrationId, enabled) => {
        try {
            const token = localStorage.getItem('token');
            const response = await apiRequest(`${API_BASE_URL}/university-integrations/${integrationId}`, 'PUT', {
                is_enabled: enabled
            }, token);
            
            if (response) {
                alert(`Integration ${enabled ? 'enabled' : 'disabled'} successfully!`);
                fetchData(); // Refresh the list
            }
        } catch (err) {
            alert(`Failed to ${enabled ? 'enable' : 'disable'} integration`);
            console.error('Error updating integration status:', err);
        }
    };

    const handleCreateIntegration = async () => {
        try {
            setErrors({});
            const token = localStorage.getItem('token');
            
            // Only uni_admins should create university integrations
            // Super admins create templates, not university integrations
            if (userRole !== 'uni_admin') {
                setErrors({ general: 'Only university administrators can create university integrations. Super admins create templates instead.' });
                return;
            }
            
            const university_id = uniAdminUniversityId;
            if (!university_id) {
                setErrors({ university_id: 'University ID not found. Please log in again.' });
                return;
            }
            
            const requestData = {
                ...formData,
                university_id: university_id
            };
            
            const response = await apiRequest(`${API_BASE_URL}/university-integrations/`, 'POST', requestData, token);
            if (response.success || response.id) { // Handle both success formats
                setShowCreateModal(false);
                resetForm();
                fetchData();
            } else {
                setErrors(response.errors || {});
            }
        } catch (error) {
            console.error('Error creating integration:', error);
            setErrors({ general: 'Failed to create integration' });
        }
    };

    const handleUpdateIntegrationBasic = async (basicData) => {
        try {
            setIsSubmitting(true);
            setErrors({});
            
            const token = localStorage.getItem('token');
            const response = await apiRequest(`${API_BASE_URL}/university-integrations/${selectedIntegration.id}`, 'PUT', basicData, token);
            
            if (response.success) {
                alert(`Integration "${basicData.name}" updated successfully!`);
                setShowEditBasicModal(false);
                setSelectedIntegration(null);
                fetchData();
            } else {
                console.error('Backend returned errors:', response.errors);
                setErrors(response.errors || {});
                alert('Failed to update integration. Please check the form for errors.');
            }
        } catch (error) {
            console.error('Error updating integration:', error);
            setErrors({ general: 'Failed to update integration' });
            alert('Failed to update integration. Please try again.');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleUpdateIntegrationSync = async (syncData) => {
        try {
            setIsSubmitting(true);
            setErrors({});
            
            // Send sync data in the proper format expected by the backend
            const updateData = {
                sync_enabled: syncData.sync_enabled,
                sync_schedule: syncData.sync_settings
            };
            
            console.log('Sending sync update data:', updateData);
            
            const token = localStorage.getItem('token');
            const response = await apiRequest(`${API_BASE_URL}/university-integrations/${selectedIntegration.id}`, 'PUT', updateData, token);
            
            // If we get here, the request was successful (no exception thrown)
            setShowEditSyncModal(false);
            setSelectedIntegration(null);
            
            // Force a fresh fetch to get updated data
            console.log('Fetching updated data...');
            await fetchData();
            
            // Show success message (you can add a toast notification here if you have one)
            console.log(`Sync settings updated successfully!${syncData.sync_enabled ? ' Sync is enabled and will run according to the schedule.' : ''}`);
        } catch (error) {
            console.error('Error updating sync settings:', error);
            setErrors({ general: 'Failed to update sync settings' });
            alert('Failed to update sync settings. Please try again.');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleUpdateIntegrationConfig = async (configData) => {
        try {
            setIsSubmitting(true);
            setErrors({});
            
            const token = localStorage.getItem('token');
            const response = await apiRequest(`${API_BASE_URL}/university-integrations/${selectedIntegration.id}`, 'PUT', configData, token);
            
            // The backend returns the integration object directly on success
            if (response && response.id) {
                alert(`Configuration updated successfully!`);
                setShowEditConfigModal(false);
                setSelectedIntegration(null);
                fetchData();
            } else {
                console.error('Backend returned errors:', response.errors);
                setErrors(response.errors || {});
                alert('Failed to update configuration. Please check the form for errors.');
            }
        } catch (error) {
            console.error('Error updating configuration:', error);
            setErrors({ general: 'Failed to update configuration' });
            alert('Failed to update configuration. Please try again.');
        } finally {
            setIsSubmitting(false);
        }
    };

    const openDeleteModal = (integration) => {
        setIntegrationToDelete(integration);
        setDeleteConfirmText("");
        setShowDeleteModal(true);
    };

    const closeDeleteModal = () => {
        setShowDeleteModal(false);
        setIntegrationToDelete(null);
        setDeleteConfirmText("");
    };

    const handleDeleteIntegration = async () => {
        if (!integrationToDelete) return;
        
        try {
            const token = localStorage.getItem('token');
            const response = await apiRequest(`${API_BASE_URL}/university-integrations/${integrationToDelete.id}`, 'DELETE', null, token);
            if (response.message) {
                fetchData();
                closeDeleteModal();
            }
        } catch (error) {
            console.error('Error deleting integration:', error);
            alert('Failed to delete integration. Please try again.');
        }
    };

    const handleTestIntegration = async (integrationId) => {
        try {
            setTestResults(null);
            const token = localStorage.getItem('token');
            const response = await apiRequest(`${API_BASE_URL}/sync-management/integrations/${integrationId}/test/comprehensive`, 'POST', null, token);
            if (response.success) {
                setTestResults(response.data);
            }
        } catch (error) {
            console.error('Error testing integration:', error);
            setTestResults({ success: false, message: 'Test failed' });
        }
    };

    const handleManualSync = async (integrationId) => {
        try {
            // Set loading state for this specific integration
            setSyncLoading(prev => ({ ...prev, [integrationId]: true }));
            setSyncResults(null);
            
            const token = localStorage.getItem('token');
            const response = await apiRequest(`${API_BASE_URL}/sync-management/integrations/${integrationId}/sync`, 'POST', null, token);
            if (response.success) {
                setSyncResults(response.data);
                loadSyncJobs(); // Refresh sync jobs
            } else {
                setSyncResults({ success: false, message: response.message || 'Sync failed' });
            }
        } catch (error) {
            console.error('Error syncing integration:', error);
            setSyncResults({ success: false, message: error.message || 'Sync failed' });
        } finally {
            // Clear loading state for this integration
            setSyncLoading(prev => ({ ...prev, [integrationId]: false }));
        }
    };



    const openCreateModal = () => {
        resetForm();
        setShowCreateModal(true);
    };

    const openEditBasicModal = (integration) => {
        setSelectedIntegration(integration);
        setShowEditBasicModal(true);
    };

    const openEditSyncModal = (integration) => {
        setSelectedIntegration(integration);
        setShowEditSyncModal(true);
    };

    const openEditConfigModal = (integration) => {
        setSelectedIntegration(integration);
        setShowEditConfigModal(true);
    };

    const openTestModal = (integration) => {
        setSelectedIntegration(integration);
        setShowTestModal(true);
        handleTestIntegration(integration.id);
    };

    const openSyncModal = (integration) => {
        setSelectedIntegration(integration);
        setShowSyncModal(true);
        // Don't automatically trigger sync - let user click the button
    };





    const openSyncHistoryModal = (integration) => {
        setSelectedIntegrationForHistory(integration);
        setActiveTab('sync-history');
        fetchSyncHistory(integration.id);
    };

    const resetForm = () => {
        setFormData({
            name: '',
            description: '',
            template_id: '',
            configuration: {},
            is_active: true,
            sync_enabled: false,
            sync_settings: {
                mode: 'single',
                batch_size: 50,
                interval_minutes: 15,
                records_per_interval: 15,
                rate_limit: {
                    requests_per_minute: 60
                }
            }
        });
        setSelectedTemplate(null);
        setErrors({});
    };

    const handleTemplateSelect = (templateId) => {
        const template = templates.find(t => t.id === templateId);
        setSelectedTemplate(template);
        setFormData(prev => ({
            ...prev,
            template_id: templateId,
            configuration: template ? template.configuration : {}
        }));
    };

    const updateConfiguration = (section, field, value) => {
        setFormData(prev => ({
            ...prev,
            configuration: {
                ...prev.configuration,
                [section]: {
                    ...prev.configuration[section],
                    [field]: value
                }
            }
        }));
    };

    const updateSyncSettings = (field, value) => {
        setFormData(prev => ({
            ...prev,
            sync_settings: {
                ...prev.sync_settings,
                [field]: value
            }
        }));
    };

    const updateNestedSyncSettings = (section, field, value) => {
        setFormData(prev => ({
            ...prev,
            sync_settings: {
                ...prev.sync_settings,
                [section]: {
                    ...prev.sync_settings[section],
                    [field]: value
                }
            }
        }));
    };

    const getTemplateName = (templateId) => {
        const template = templates.find(t => t.id === templateId);
        return template ? template.display_name : 'Unknown Template';
    };

    const getStatusBadge = (integration) => {
        if (!integration.is_active) {
            return <span className="status-badge status-inactive">Inactive</span>;
        }
        if (integration.sync_enabled) {
            return <span className="status-badge status-active-sync">Active & Syncing</span>;
        }
        return <span className="status-badge status-active">Active</span>;
    };

    const getSyncStatusBadge = (status) => {
        const statusColors = {
            'pending': 'sync-warning',
            'running': 'sync-info',
            'completed': 'sync-success',
            'failed': 'sync-danger'
        };
        return <span className={`sync-badge ${statusColors[status] || 'sync-secondary'}`}>{status}</span>;
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        
        try {
            // Debug: Log the original date string
            console.log('Original date string:', dateString);
            
            // Ensure the date string is treated as UTC
            // If the date string doesn't end with 'Z' (UTC indicator), add it
            let utcDateString = dateString;
            if (!utcDateString.endsWith('Z') && !utcDateString.includes('+')) {
                utcDateString = utcDateString + 'Z';
            }
            
            console.log('UTC date string:', utcDateString);
            
            // Create a Date object from the UTC string
            const date = new Date(utcDateString);
            
            console.log('Date object:', date);
            console.log('Date ISO string:', date.toISOString());
            
            // Check if the date is valid
            if (isNaN(date.getTime())) {
                console.warn('Invalid date string:', dateString);
                return 'Invalid Date';
            }
            
            // Get user's timezone
            const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
            console.log('User timezone:', userTimezone);
            
            // Format the date in user's local timezone
            const formattedDate = date.toLocaleString('en-US', {
                timeZone: userTimezone,
                year: 'numeric',
                month: '2-digit',
                day: '2-digit',
                hour: '2-digit',
                minute: '2-digit',
                second: '2-digit',
                hour12: false
            });
            
            // Get timezone abbreviation
            const timezoneAbbr = new Intl.DateTimeFormat('en-US', {
                timeZone: userTimezone,
                timeZoneName: 'short'
            }).formatToParts(date).find(part => part.type === 'timeZoneName')?.value || '';
            
            const result = `${formattedDate} ${timezoneAbbr}`;
            console.log('Final formatted result:', result);
            
            return result;
        } catch (error) {
            console.error('Error formatting date:', error, 'Input:', dateString);
            return 'Invalid Date';
        }
    };

    // Utility function to show UTC time for debugging
    const formatDateUTC = (dateString) => {
        if (!dateString) return 'N/A';
        
        try {
            const date = new Date(dateString);
            if (isNaN(date.getTime())) {
                return 'Invalid Date';
            }
            
            return date.toLocaleString('en-US', {
                timeZone: 'UTC',
                year: 'numeric',
                month: '2-digit',
                day: '2-digit',
                hour: '2-digit',
                minute: '2-digit',
                second: '2-digit',
                hour12: false
            }) + ' UTC';
        } catch (error) {
            console.error('Error formatting UTC date:', error);
            return 'Invalid Date';
        }
    };

    // Render sync settings configuration
    const renderSyncSettingsConfig = () => {
        return (
            <div className="sync-settings-config">
                <div className="config-header">
                    <h3>Sync Settings</h3>
                    <div className="info-tooltip">
                        <i className="fas fa-info-circle"></i>
                        <span className="tooltip-text">Configure automated sync behavior</span>
                    </div>
                </div>

                <div className="form-group">
                    <label className="checkbox-label">
                        <input
                            type="checkbox"
                            checked={formData.sync_enabled}
                            onChange={(e) => setFormData(prev => ({ ...prev, sync_enabled: e.target.checked }))}
                        />
                        <span className="checkmark">Enable Scheduled Sync</span>
                    </label>
                </div>

                {formData.sync_enabled && (
                    <div className="sync-config-panel">
                        <div className="form-group">
                            <label>Sync Mode</label>
                            <select
                                className="form-control"
                                value={formData.sync_settings.mode || 'single'}
                                onChange={(e) => updateSyncSettings('mode', e.target.value)}
                            >
                                <option value="single">Single Record</option>
                                <option value="batch">Batch Processing</option>
                            </select>
                        </div>

                        {formData.sync_settings.mode === 'batch' && (
                            <div className="form-group">
                                <label>Batch Size</label>
                                <input
                                    type="number"
                                    className="form-control"
                                    value={formData.sync_settings.batch_size || 50}
                                    onChange={(e) => updateSyncSettings('batch_size', parseInt(e.target.value) || 50)}
                                    min="1"
                                    max="1000"
                                    placeholder="50"
                                />
                            </div>
                        )}

                        <div className="form-group">
                            <label>Interval (minutes)</label>
                            <input
                                type="number"
                                className="form-control"
                                value={formData.sync_settings.interval_minutes || 15}
                                onChange={(e) => updateSyncSettings('interval_minutes', parseInt(e.target.value) || 15)}
                                min="1"
                                max="1440"
                                placeholder="15"
                            />
                        </div>

                        <div className="form-group">
                            <label>Records Per Interval</label>
                            <input
                                type="number"
                                className="form-control"
                                value={formData.sync_settings.records_per_interval || ''}
                                onChange={(e) => updateSyncSettings('records_per_interval', e.target.value ? parseInt(e.target.value) : null)}
                                min="1"
                                max="10000"
                                placeholder="Leave empty for no limit"
                            />
                        </div>

                        <div className="form-group">
                            <label>Rate Limit (requests per minute)</label>
                            <input
                                type="number"
                                className="form-control"
                                value={formData.sync_settings.rate_limit?.requests_per_minute || 60}
                                onChange={(e) => updateNestedSyncSettings('rate_limit', 'requests_per_minute', parseInt(e.target.value) || 60)}
                                min="1"
                                max="1000"
                                placeholder="60"
                            />
                        </div>
                    </div>
                )}
            </div>
        );
    };

    if (loading) {
        return (
            <div className="dashboard">
                <Sidebar />
                <div className="main-content">
                    <DashboardHeader />
                    <div className="content">
                        <div className="loading-container">
                            <div className="loading-spinner"></div>
                            <p>Loading integrations...</p>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="dashboard">
            <Sidebar />
            <div className="main-content">
                <div className="page-header">
                    <div className="page-title">
                        <h1>Integration Management</h1>
                        <p className="page-description">
                            {userRole === 'uni_admin' 
                                ? 'Manage your university integrations and synchronization settings' 
                                : 'View and manage university integrations across all universities'
                            }
                        </p>
                    </div>
                    {userRole === 'uni_admin' && (
                        <button className="btn btn-primary btn-create" onClick={openCreateModal}>
                            <i className="fas fa-plus"></i>
                            Create Integration
                        </button>
                    )}
                </div>

                <div className="breadcrumb">
                    <a href="/dashboard">Dashboard</a>
                    <span className="breadcrumb-separator">
                        <i className="fas fa-chevron-right"></i>
                    </span>
                    <span className="breadcrumb-current">Integration Management</span>
                </div>

                <div className="tab-navigation">
                    <button
                        className={`tab-button ${activeTab === 'integrations' ? 'active' : ''}`}
                        onClick={() => setActiveTab('integrations')}
                    >
                        <i className="fas fa-plug"></i>
                        <span>Integrations</span>
                    </button>
                    <button
                        className={`tab-button ${activeTab === 'sync' ? 'active' : ''}`}
                        onClick={() => {
                            setActiveTab('sync');
                            loadSyncJobs();
                        }}
                    >
                        <i className="fas fa-sync-alt"></i>
                        <span>Sync Management</span>
                    </button>
                    <button
                        className={`tab-button ${activeTab === 'sync-history' ? 'active' : ''}`}
                        onClick={() => setActiveTab('sync-history')}
                    >
                        <i className="fas fa-history"></i>
                        <span>Sync History</span>
                    </button>
                </div>

                <div className="content-container">
                    {activeTab === 'integrations' && (
                        <div className="integrations-section">
                            {integrations.length > 0 ? (
                                <div className="integrations-grid">
                                    {integrations.map(integration => (
                                        <div key={integration.id} className="integration-card">
                                            <div className="card-header">
                                                <div className="card-title">
                                                    <h3>{integration.name}</h3>
                                                    {getStatusBadge(integration)}
                                                </div>
                                            </div>

                                            <div className="card-body">
                                                <p className="card-description">{integration.description}</p>

                                                <div className="card-metadata">
                                                    <div className="metadata-item">
                                                        <span className="metadata-label">Template:</span>
                                                        <span className="metadata-value">{getTemplateName(integration.template_id)}</span>
                                                    </div>
                                                    <div className="metadata-item">
                                                        <span className="metadata-label">Sync Status:</span>
                                                        <span className={`metadata-value ${integration.sync_enabled ? 'enabled' : 'disabled'}`}>
                                                            {integration.sync_enabled ? 'Enabled' : 'Disabled'}
                                                        </span>
                                                    </div>
                                                </div>
                                            </div>

                                            <div className="card-actions">
                                                <button
                                                    className="action-btn btn-config"
                                                    onClick={() => openEditConfigModal(integration)}
                                                    title="View/Edit Configuration"
                                                >
                                                    <i className="fas fa-cogs"></i>
                                                </button>
                                                <button
                                                    className="action-btn btn-edit"
                                                    onClick={() => openEditBasicModal(integration)}
                                                    title="Edit Basic Info"
                                                >
                                                    <i className="fas fa-edit"></i>
                                                </button>

                                                <button
                                                    className="action-btn btn-settings"
                                                    onClick={() => openEditSyncModal(integration)}
                                                    title="Sync Schedule Settings"
                                                >
                                                    <i className="fas fa-clock"></i>
                                                </button>
                                                <button
                                                    className="action-btn btn-history"
                                                    onClick={() => openSyncHistoryModal(integration)}
                                                    title="View Sync History"
                                                >
                                                    <i className="fas fa-history"></i>
                                                </button>
                                                <button
                                                    className="action-btn btn-delete"
                                                    onClick={() => openDeleteModal(integration)}
                                                    title="Delete Integration"
                                                >
                                                    <i className="fas fa-trash"></i>
                                                </button>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <div className="empty-state">
                                    <div className="empty-icon">
                                        <i className="fas fa-plug"></i>
                                    </div>
                                    <h3>No Integrations</h3>
                                    <p>Create your first integration to get started.</p>
                                    <button className="btn btn-primary" onClick={openCreateModal}>
                                        <i className="fas fa-plus"></i>
                                        Create Integration
                                    </button>
                                </div>
                            )}
                        </div>
                    )}

                    {activeTab === 'sync' && (
                        <div className="sync-section">
                            <div className="sync-overview">
                                <h2>Sync Management</h2>
                                <div className="stats-grid">
                                    <div className="stat-card">
                                        <div className="stat-icon">
                                            <i className="fas fa-sync-alt"></i>
                                        </div>
                                        <div className="stat-content">
                                            <h3>{integrations.filter(i => i.sync_enabled).length}</h3>
                                            <p>Active Syncs</p>
                                        </div>
                                    </div>
                                    <div className="stat-card">
                                        <div className="stat-icon">
                                            <i className="fas fa-plug"></i>
                                        </div>
                                        <div className="stat-content">
                                            <h3>{integrations.length}</h3>
                                            <p>Total Integrations</p>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="integrations-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(400px, 1fr))', gap: '20px', padding: '20px' }}>
                                {integrations.map((integration) => (
                                    <div key={integration.id} className="integration-card" style={{ 
                                        border: '1px solid #ddd', 
                                        borderRadius: '8px', 
                                        padding: '20px', 
                                        backgroundColor: '#fff',
                                        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                                        wordWrap: 'break-word',
                                        overflowWrap: 'break-word'
                                    }}>
                                        <div className="integration-header">
                                            <h3>{integration.name}</h3>
                                            <span 
                                                className={`status-badge ${integration.sync_enabled ? 'enabled' : 'disabled'}`}
                                            >
                                                {integration.sync_enabled ? 'Enabled' : 'Disabled'}
                                            </span>
                                        </div>
                                        
                                        <div className="integration-details" style={{ marginBottom: '15px' }}>
                                            <p style={{ margin: '5px 0', wordBreak: 'break-word' }}><strong>University:</strong> {integration.university?.name || 'Unknown'}</p>
                                            <p style={{ margin: '5px 0', wordBreak: 'break-word' }}><strong>Template:</strong> {integration.template?.name || 'Unknown'}</p>
                                            <p style={{ margin: '5px 0' }}><strong>Last Sync:</strong> {formatDate(integration.last_sync_run)}</p>
                                            <p style={{ margin: '5px 0' }}><strong>Next Sync:</strong> {formatDate(integration.next_sync_run)}</p>
                                            
                                            {integration.sync_schedule && (
                                                <div className="sync-settings" style={{ marginTop: '10px', padding: '10px', backgroundColor: '#f8f9fa', borderRadius: '4px' }}>
                                                    <p style={{ margin: '5px 0' }}><strong>Schedule Type:</strong> {integration.sync_schedule.schedule_type === 'cron' ? 'Time-based' : 'Interval-based'}</p>
                                                    {integration.sync_schedule.schedule_type === 'interval' ? (
                                                        <p style={{ margin: '5px 0' }}><strong>Interval:</strong> {integration.sync_schedule.interval_minutes || 15} minutes</p>
                                                    ) : (
                                                        <p style={{ margin: '5px 0' }}><strong>Time:</strong> {integration.sync_schedule.hour || 2}:{String(integration.sync_schedule.minute || 0).padStart(2, '0')} {integration.sync_schedule.timezone || 'UTC'}</p>
                                                    )}
                                                    <p style={{ margin: '5px 0' }}><strong>Batch Size:</strong> {integration.sync_schedule.batch_size || 50}</p>
                                                    <p style={{ margin: '5px 0' }}><strong>Mode:</strong> {integration.sync_schedule.mode || 'single'}</p>
                                                </div>
                                            )}
                                        </div>
                                        
                                        <div className="integration-actions" style={{ 
                                            display: 'flex', 
                                            flexWrap: 'wrap', 
                                            gap: '8px', 
                                            marginTop: '15px' 
                                        }}>
                                            <button 
                                                className="btn btn-primary"
                                                style={{ fontSize: '12px', padding: '6px 12px' }}
                                                onClick={() => handleManualSync(integration.id)}
                                                disabled={syncLoading[integration.id]}
                                            >
                                                <i className={`fas fa-sync ${syncLoading[integration.id] ? 'fa-spin' : ''}`}></i> 
                                                {syncLoading[integration.id] ? 'Syncing...' : 'Manual Sync'}
                                            </button>
                                            
                                            <button 
                                                className="btn btn-secondary"
                                                style={{ fontSize: '12px', padding: '6px 12px' }}
                                                onClick={() => openSyncHistoryModal(integration)}
                                            >
                                                <i className="fas fa-history"></i> History
                                            </button>
                                            
                                            <button 
                                                className="btn btn-info"
                                                style={{ fontSize: '12px', padding: '6px 12px' }}
                                                onClick={() => {
                                                    setSelectedIntegration(integration);
                                                    setScheduleConfig({
                                                        type: integration.sync_schedule?.schedule_type || 'interval',
                                                        interval_minutes: integration.sync_schedule?.interval_minutes || 15,
                                                        hour: integration.sync_schedule?.hour || 2,
                                                        minute: integration.sync_schedule?.minute || 0,
                                                        timezone: integration.sync_schedule?.timezone || 'UTC',
                                                        enabled: integration.sync_enabled
                                                    });
                                                    setShowScheduleModal(true);
                                                }}
                                            >
                                                <i className="fas fa-cog"></i> Schedule
                                            </button>
                                            
                                            {integration.sync_enabled ? (
                                                <button 
                                                    className="btn btn-warning"
                                                    style={{ fontSize: '12px', padding: '6px 12px' }}
                                                    onClick={() => updateIntegrationEnabledStatus(integration.id, false)}
                                                >
                                                    <i className="fas fa-pause"></i> Disable
                                                </button>
                                            ) : (
                                                <button 
                                                    className="btn btn-success"
                                                    style={{ fontSize: '12px', padding: '6px 12px' }}
                                                    onClick={() => updateIntegrationEnabledStatus(integration.id, true)}
                                                >
                                                    <i className="fas fa-play"></i> Enable
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>

                            {integrations.length === 0 && (
                                <div className="no-integrations">
                                    <p>No integrations found. Create an integration first to manage sync settings.</p>
                                </div>
                            )}
                        </div>
                    )}

                    {activeTab === 'sync-history' && (
                        <div className="sync-history-section">
                            <div className="sync-history-header">
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                                    <h2>Sync History</h2>
                                    <div style={{ fontSize: '12px', color: '#666', display: 'flex', alignItems: 'center', gap: '4px' }}>
                                        <i className="fas fa-clock" style={{ fontSize: '10px' }}></i>
                                        Times shown in {Intl.DateTimeFormat().resolvedOptions().timeZone}
                                    </div>
                                </div>
                                {selectedIntegrationForHistory && (
                                    <div className="selected-integration-info">
                                        <h3>{selectedIntegrationForHistory.name}</h3>
                                        <p>University: {selectedIntegrationForHistory.university?.name || 'Unknown'}</p>
                                        <p>Template: {getTemplateName(selectedIntegrationForHistory.template_id)}</p>
                                    </div>
                                )}
                            </div>

                            <div className="sync-history-content">
                                {selectedIntegrationForHistory ? (
                                    syncHistory.length > 0 ? (
                                        <div className="sync-history-list">
                                            {syncHistory.map((log, index) => (
                                                <SyncHistoryItem 
                                                    key={index} 
                                                    log={log} 
                                                    formatDate={formatDate}
                                                />
                                            ))}
                                        </div>
                                    ) : (
                                        <div className="empty-state">
                                            <div className="empty-icon">
                                                <i className="fas fa-history"></i>
                                            </div>
                                            <h3>No Sync History</h3>
                                            <p>No sync history available for this integration.</p>
                                        </div>
                                    )
                                ) : (
                                    <div className="empty-state">
                                        <div className="empty-icon">
                                            <i className="fas fa-history"></i>
                                        </div>
                                        <h3>Select an Integration</h3>
                                        <p>Click the history button on any integration to view its sync history.</p>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Create Integration Modal */}
            {showCreateModal && (
                <CreateIntegrationModal
                    onClose={() => setShowCreateModal(false)}
                    onSave={handleCreateIntegration}
                    templates={templates}
                    formData={formData}
                    setFormData={setFormData}
                    errors={errors}
                    setErrors={setErrors}
                />
            )}

            {/* Edit Integration Basic Modal */}
            {showEditBasicModal && (
                <EditIntegrationBasicModal
                    onClose={() => setShowEditBasicModal(false)}
                    onSave={handleUpdateIntegrationBasic}
                    integration={selectedIntegration}
                    errors={errors}
                    setErrors={setErrors}
                    isSubmitting={isSubmitting}
                />
            )}

            {/* Edit Integration Sync Modal */}
            {showEditSyncModal && (
                <EditIntegrationSyncModal
                    onClose={() => setShowEditSyncModal(false)}
                    onSave={handleUpdateIntegrationSync}
                    integration={selectedIntegration}
                    errors={errors}
                    setErrors={setErrors}
                    isSubmitting={isSubmitting}
                />
            )}

            {/* Edit Integration Config Modal */}
            {showEditConfigModal && (
                <EditIntegrationConfigModal
                    onClose={() => setShowEditConfigModal(false)}
                    onSave={handleUpdateIntegrationConfig}
                    integration={selectedIntegration}
                    errors={errors}
                    setErrors={setErrors}
                    isSubmitting={isSubmitting}
                />
            )}





            {/* Test Results Modal */}
            {showTestModal && (
                <div className="modal-overlay">
                    <div className="modal-container">
                        <div className="modal-header">
                            <h2>Test Results - {selectedIntegration?.name}</h2>
                            <button className="modal-close" onClick={() => setShowTestModal(false)}>
                                <i className="fas fa-times"></i>
                            </button>
                        </div>

                        <div className="modal-body">
                            {testResults ? (
                                <div className="test-results">
                                    <div className={`alert alert-${testResults.success ? 'success' : 'danger'}`}>
                                        <i className={`fas fa-${testResults.success ? 'check-circle' : 'exclamation-triangle'}`}></i>
                                        {testResults.message}
                                    </div>
                                    {testResults.details && (
                                        <div className="test-details">
                                            <h4>Test Details</h4>
                                            <pre className="code-block">
                                                {JSON.stringify(testResults.details, null, 2)}
                                            </pre>
                                        </div>
                                    )}
                                </div>
                            ) : (
                                <div className="loading-container">
                                    <div className="loading-spinner"></div>
                                    <p>Running comprehensive test...</p>
                                </div>
                            )}
                        </div>

                        <div className="modal-footer">
                            <button className="btn btn-secondary" onClick={() => setShowTestModal(false)}>
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Sync Results Modal */}
            {showSyncModal && (
                <div className="modal-overlay">
                    <div className="modal-container">
                        <div className="modal-header">
                            <h2>Sync Results - {selectedIntegration?.name}</h2>
                            <button className="modal-close" onClick={() => setShowSyncModal(false)}>
                                <i className="fas fa-times"></i>
                            </button>
                        </div>

                        <div className="modal-body">
                            {syncResults ? (
                                <div className="sync-results">
                                    <div className={`alert alert-${syncResults.success ? 'success' : 'danger'}`}>
                                        <i className={`fas fa-${syncResults.success ? 'check-circle' : 'exclamation-triangle'}`}></i>
                                        {syncResults.message}
                                    </div>

                                    <div className="sync-metrics">
                                        <div className="metrics-grid">
                                            <div className="metric-item">
                                                <span className="metric-label">Records Processed</span>
                                                <span className="metric-value">{syncResults.records_processed || 0}</span>
                                            </div>
                                            <div className="metric-item">
                                                <span className="metric-label">Successful</span>
                                                <span className="metric-value success">{syncResults.records_successful || 0}</span>
                                            </div>
                                            <div className="metric-item">
                                                <span className="metric-label">Failed</span>
                                                <span className="metric-value error">{syncResults.records_failed || 0}</span>
                                            </div>
                                            <div className="metric-item">
                                                <span className="metric-label">Duration</span>
                                                <span className="metric-value">{syncResults.duration_seconds || 0}s</span>
                                            </div>
                                        </div>
                                    </div>

                                    {syncResults.errors && syncResults.errors.length > 0 && (
                                        <div className="sync-errors">
                                            <h4>Errors Encountered</h4>
                                            <div className="errors-list">
                                                {syncResults.errors.map((error, index) => (
                                                    <div key={index} className="error-item">
                                                        <i className="fas fa-exclamation-circle"></i>
                                                        <span>{error}</span>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            ) : (
                                <div className="loading-container">
                                    <div className="loading-spinner"></div>
                                    <p>Initiating manual sync...</p>
                                </div>
                            )}
                        </div>

                        <div className="modal-footer">
                            <button className="btn btn-secondary" onClick={() => setShowSyncModal(false)}>
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            )}



            {/* Delete Confirmation Modal */}
            {showDeleteModal && (
                <div className="modal-overlay">
                    <div className="modal-container">
                        <div className="modal-header">
                            <h2>Confirm Delete</h2>
                            <button className="modal-close" onClick={closeDeleteModal}>
                                <i className="fas fa-times"></i>
                            </button>
                        </div>

                        <div className="modal-body">
                            <p>To delete the integration <b>{integrationToDelete?.name}</b>, type <b>DELETE</b> below and click Delete.</p>
                            <input
                                type="text"
                                value={deleteConfirmText}
                                onChange={e => setDeleteConfirmText(e.target.value)}
                                placeholder="Type 'DELETE' to confirm"
                                style={{ 
                                    width: '100%', 
                                    padding: '12px', 
                                    margin: '15px 0',
                                    border: '1px solid #ddd',
                                    borderRadius: '4px',
                                    fontSize: '14px'
                                }}
                            />
                        </div>

                        <div className="modal-footer">
                            <button 
                                className="btn btn-secondary" 
                                onClick={closeDeleteModal}
                                style={{ padding: '10px 20px' }}
                            >
                                Cancel
                            </button>
                            <button
                                className="btn btn-danger"
                                onClick={handleDeleteIntegration}
                                disabled={deleteConfirmText !== 'DELETE'}
                                style={{ 
                                    padding: '10px 20px',
                                    backgroundColor: deleteConfirmText === 'DELETE' ? '#dc3545' : '#6c757d',
                                    borderColor: deleteConfirmText === 'DELETE' ? '#dc3545' : '#6c757d'
                                }}
                            >
                                <i className="fas fa-trash"></i>
                                Delete Integration
                            </button>
                        </div>
                    </div>
                </div>
            )}



            {/* Schedule Modal */}
            {showScheduleModal && selectedIntegration && (
                <div className="modal-overlay" onClick={() => setShowScheduleModal(false)}>
                    <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <h3>Configure Sync Schedule - {selectedIntegration.name}</h3>
                            <button className="close-btn" onClick={() => setShowScheduleModal(false)}>×</button>
                        </div>
                        <div className="modal-body">
                            <div className="form-group" style={{ marginBottom: '20px' }}>
                                <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>Schedule Type:</label>
                                <select
                                    value={scheduleConfig.type}
                                    onChange={(e) => setScheduleConfig(prev => ({
                                        ...prev,
                                        type: e.target.value
                                    }))}
                                    style={{ 
                                        width: '100%', 
                                        padding: '10px', 
                                        border: '1px solid #ddd', 
                                        borderRadius: '4px',
                                        fontSize: '14px'
                                    }}
                                >
                                    <option value="interval">Interval-based (every X minutes)</option>
                                    <option value="cron">Time-based (daily at specific time)</option>
                                </select>
                            </div>
                            
                            {scheduleConfig.type === 'interval' && (
                                <div className="form-group" style={{ marginBottom: '20px' }}>
                                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>Sync Interval (minutes):</label>
                                    <input
                                        type="number"
                                        value={scheduleConfig.interval_minutes}
                                        onChange={(e) => setScheduleConfig(prev => ({
                                            ...prev,
                                            interval_minutes: parseInt(e.target.value) || 15
                                        }))}
                                        min="1"
                                        max="1440"
                                        style={{ 
                                            width: '100%', 
                                            padding: '10px', 
                                            border: '1px solid #ddd', 
                                            borderRadius: '4px',
                                            fontSize: '14px'
                                        }}
                                    />
                                </div>
                            )}
                            
                            {scheduleConfig.type === 'cron' && (
                                <>
                                    <div className="form-group" style={{ marginBottom: '20px' }}>
                                        <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>Hour (24-hour format):</label>
                                        <input
                                            type="number"
                                            value={scheduleConfig.hour}
                                            onChange={(e) => setScheduleConfig(prev => ({
                                                ...prev,
                                                hour: parseInt(e.target.value) || 2
                                            }))}
                                            min="0"
                                            max="23"
                                            style={{ 
                                                width: '100%', 
                                                padding: '10px', 
                                                border: '1px solid #ddd', 
                                                borderRadius: '4px',
                                                fontSize: '14px'
                                            }}
                                        />
                                    </div>
                                    <div className="form-group" style={{ marginBottom: '20px' }}>
                                        <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>Minute:</label>
                                        <input
                                            type="number"
                                            value={scheduleConfig.minute}
                                            onChange={(e) => setScheduleConfig(prev => ({
                                                ...prev,
                                                minute: parseInt(e.target.value) || 0
                                            }))}
                                            min="0"
                                            max="59"
                                            style={{ 
                                                width: '100%', 
                                                padding: '10px', 
                                                border: '1px solid #ddd', 
                                                borderRadius: '4px',
                                                fontSize: '14px'
                                            }}
                                        />
                                    </div>
                                    <div className="form-group" style={{ marginBottom: '20px' }}>
                                        <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>Timezone:</label>
                                        <select
                                            value={scheduleConfig.timezone}
                                            onChange={(e) => setScheduleConfig(prev => ({
                                                ...prev,
                                                timezone: e.target.value
                                            }))}
                                            style={{ 
                                                width: '100%', 
                                                padding: '10px', 
                                                border: '1px solid #ddd', 
                                                borderRadius: '4px',
                                                fontSize: '14px'
                                            }}
                                        >
                                            <option value="UTC">UTC</option>
                                            <option value="America/New_York">Eastern Time</option>
                                            <option value="America/Chicago">Central Time</option>
                                            <option value="America/Denver">Mountain Time</option>
                                            <option value="America/Los_Angeles">Pacific Time</option>
                                            <option value="Europe/London">London</option>
                                            <option value="Europe/Paris">Paris</option>
                                            <option value="Asia/Tokyo">Tokyo</option>
                                        </select>
                                    </div>
                                </>
                            )}
                            
                            <div className="form-group" style={{ marginBottom: '20px' }}>
                                <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    <input
                                        type="checkbox"
                                        checked={scheduleConfig.enabled}
                                        onChange={(e) => setScheduleConfig(prev => ({
                                            ...prev,
                                            enabled: e.target.checked
                                        }))}
                                    />
                                    <span style={{ fontWeight: 'bold' }}>Enable Sync</span>
                                </label>
                            </div>
                        </div>
                        <div className="modal-footer">
                            <button 
                                className="btn btn-secondary" 
                                onClick={() => setShowScheduleModal(false)}
                                style={{ padding: '10px 20px' }}
                            >
                                Cancel
                            </button>
                            <button 
                                className="btn btn-primary" 
                                onClick={() => updateIntegrationSyncSettings(selectedIntegration.id, scheduleConfig)}
                                style={{ padding: '10px 20px' }}
                            >
                                Save Schedule
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <style jsx>{`
                /* Professional Integration Management Styles */
                
                .page-header {
                    display: flex;
                    justify-content: space-between;
                    align-items: flex-start;
                    margin-bottom: 2rem;
                    padding: 0 2rem;
                    border-bottom: 1px solid #e5e7eb;
                    padding-bottom: 2rem;
                }
                
                .page-title h1 {
                    font-size: 2.25rem;
                    font-weight: 700;
                    color: #111827;
                    margin: 0 0 0.5rem 0;
                }
                
                .page-description {
                    font-size: 1rem;
                    color: #6b7280;
                    margin: 0;
                }
                
                .btn-create {
                    display: flex;
                    align-items: center;
                    gap: 0.5rem;
                    padding: 0.75rem 1.5rem;
                    background: linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%);
                    border: none;
                    border-radius: 0.5rem;
                    color: white;
                    font-weight: 600;
                    transition: all 0.2s ease;
                    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
                    cursor: pointer;
                }
                
                .btn-create:hover {
                    background: linear-gradient(135deg, #1d4ed8 0%, #1e40af 100%);
                    transform: translateY(-2px);
                    box-shadow: 0 8px 15px -3px rgba(0, 0, 0, 0.2);
                }
                
                .breadcrumb {
                    display: flex;
                    align-items: center;
                    gap: 0.5rem;
                    margin: 0 2rem 2rem 2rem;
                    font-size: 0.875rem;
                    color: #6b7280;
                }
                
                .breadcrumb a {
                    color: #3b82f6;
                    text-decoration: none;
                    transition: color 0.2s ease;
                }
                
                .breadcrumb a:hover {
                    color: #1d4ed8;
                }
                
                .breadcrumb-separator {
                    color: #d1d5db;
                    font-size: 0.75rem;
                }
                
                .breadcrumb-current {
                    color: #374151;
                    font-weight: 500;
                }
                
                .tab-navigation {
                    display: flex;
                    margin: 0 2rem 2rem 2rem;
                    background: #f9fafb;
                    border-radius: 0.75rem;
                    padding: 0.25rem;
                    border: 1px solid #e5e7eb;
                }
                
                .tab-button {
                    display: flex;
                    align-items: center;
                    gap: 0.5rem;
                    padding: 0.75rem 1.5rem;
                    background: transparent;
                    border: none;
                    border-radius: 0.5rem;
                    color: #6b7280;
                    font-weight: 500;
                    cursor: pointer;
                    transition: all 0.2s ease;
                    flex: 1;
                    justify-content: center;
                }
                
                .tab-button:hover {
                    color: #374151;
                    background: #f3f4f6;
                }
                
                .tab-button.active {
                    background: white;
                    color: #3b82f6;
                    font-weight: 600;
                    box-shadow: 0 2px 4px -1px rgba(0, 0, 0, 0.1);
                }
                
                .content-container {
                    padding: 0 2rem;
                }
                
                .integrations-grid {
                    display: grid;
                    grid-template-columns: repeat(auto-fill, minmax(400px, 1fr));
                    gap: 1.5rem;
                    margin-bottom: 2rem;
                }
                
                .integration-card {
                    background: white;
                    border-radius: 1rem;
                    border: 1px solid #e5e7eb;
                    overflow: hidden;
                    transition: all 0.3s ease;
                    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
                }
                
                .integration-card:hover {
                    transform: translateY(-4px);
                    box-shadow: 0 12px 25px rgba(0, 0, 0, 0.1);
                    border-color: #d1d5db;
                }
                
                .card-header {
                    display: flex;
                    justify-content: space-between;
                    align-items: flex-start;
                    padding: 1.5rem 1.5rem 1rem 1.5rem;
                }
                
                .card-title h3 {
                    font-size: 1.25rem;
                    font-weight: 600;
                    color: #111827;
                    margin: 0 0 0.5rem 0;
                }
                
                .status-badge {
                    padding: 0.25rem 0.75rem;
                    border-radius: 9999px;
                    font-size: 0.75rem;
                    font-weight: 600;
                    text-transform: uppercase;
                    letter-spacing: 0.05em;
                }
                
                .status-active {
                    background: #dcfce7;
                    color: #166534;
                }
                
                .status-active-sync {
                    background: #dbeafe;
                    color: #1e40af;
                }
                
                .status-inactive {
                    background: #fef2f2;
                    color: #991b1b;
                }
                
                .status-badge.enabled {
                    background: #dcfce7;
                    color: #166534;
                }
                
                .status-badge.disabled {
                    background: #fef2f2;
                    color: #991b1b;
                }
                
                .card-body {
                    padding: 0 1.5rem 1rem 1.5rem;
                }
                
                .card-description {
                    color: #6b7280;
                    margin-bottom: 1rem;
                    line-height: 1.5;
                }
                
                .card-metadata {
                    display: flex;
                    flex-direction: column;
                    gap: 0.5rem;
                }
                
                .metadata-item {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    font-size: 0.875rem;
                }
                
                .metadata-label {
                    color: #9ca3af;
                    font-weight: 500;
                }
                
                .metadata-value {
                    color: #374151;
                    font-weight: 600;
                }
                
                .metadata-value.enabled {
                    color: #059669;
                }
                
                .metadata-value.disabled {
                    color: #dc2626;
                }
                
                .card-actions {
                    display: flex;
                    gap: 0.5rem;
                    padding: 1rem 1.5rem;
                    background: #f9fafb;
                    border-top: 1px solid #f3f4f6;
                }
                
                .action-btn {
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    width: 2.5rem;
                    height: 2.5rem;
                    border: 1px solid #d1d5db;
                    background: white;
                    border-radius: 0.5rem;
                    color: #6b7280;
                    cursor: pointer;
                    transition: all 0.2s ease;
                    font-size: 0.875rem;
                }
                
                .action-btn:hover {
                    transform: translateY(-1px);
                    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
                }
                
                .btn-config {
                    background: #f8fafc;
                    border-color: #475569;
                    color: #475569;
                }
                
                .btn-config:hover {
                    background: #f1f5f9;
                    border-color: #334155;
                    color: #334155;
                }
                
                .btn-edit:hover {
                    background: #f0f9ff;
                    border-color: #0ea5e9;
                    color: #0ea5e9;
                }
                
                .btn-test:hover {
                    background: #fef3c7;
                    border-color: #f59e0b;
                    color: #f59e0b;
                }
                
                .btn-sync:hover {
                    background: #ecfdf5;
                    border-color: #10b981;
                    color: #10b981;
                }
                
                .btn-settings:hover {
                    background: #f3f4f6;
                    border-color: #6b7280;
                    color: #374151;
                }
                
                .btn-delete:hover {
                    background: #fef2f2;
                    border-color: #ef4444;
                    color: #ef4444;
                }
                
                .empty-state {
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    justify-content: center;
                    padding: 4rem 2rem;
                    text-align: center;
                    background: white;
                    border-radius: 1rem;
                    border: 2px dashed #d1d5db;
                }
                
                .empty-icon {
                    font-size: 3rem;
                    color: #d1d5db;
                    margin-bottom: 1rem;
                }
                
                .empty-state h3 {
                    font-size: 1.5rem;
                    font-weight: 600;
                    color: #374151;
                    margin-bottom: 0.5rem;
                }
                
                .empty-state p {
                    color: #6b7280;
                    margin-bottom: 2rem;
                }
                
                .sync-overview {
                    margin-bottom: 3rem;
                }
                
                .sync-overview h2 {
                    font-size: 1.5rem;
                    font-weight: 600;
                    color: #111827;
                    margin-bottom: 1.5rem;
                }
                
                .stats-grid {
                    display: grid;
                    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
                    gap: 1.5rem;
                }
                
                .stat-card {
                    display: flex;
                    align-items: center;
                    gap: 1rem;
                    background: white;
                    padding: 1.5rem;
                    border-radius: 1rem;
                    border: 1px solid #e5e7eb;
                    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
                }
                
                .stat-icon {
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    width: 3rem;
                    height: 3rem;
                    background: linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%);
                    border-radius: 0.75rem;
                    color: white;
                    font-size: 1.25rem;
                }
                
                .stat-content h3 {
                    font-size: 2rem;
                    font-weight: 700;
                    color: #111827;
                    margin: 0;
                }
                
                .stat-content p {
                    color: #6b7280;
                    font-size: 0.875rem;
                    margin: 0;
                }
                
                .sync-jobs-section h2 {
                    font-size: 1.5rem;
                    font-weight: 600;
                    color: #111827;
                    margin-bottom: 1.5rem;
                }
                
                .sync-jobs-table {
                    background: white;
                    border-radius: 1rem;
                    border: 1px solid #e5e7eb;
                    overflow: hidden;
                    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
                }
                
                .table-header {
                    display: grid;
                    grid-template-columns: 1fr 120px 180px 100px 100px;
                    gap: 1rem;
                    padding: 1rem 1.5rem;
                    background: #f9fafb;
                    border-bottom: 1px solid #e5e7eb;
                    font-weight: 600;
                    color: #374151;
                    font-size: 0.875rem;
                }
                
                .table-row {
                    display: grid;
                    grid-template-columns: 1fr 120px 180px 100px 100px;
                    gap: 1rem;
                    padding: 1rem 1.5rem;
                    border-bottom: 1px solid #f3f4f6;
                    transition: background 0.2s ease;
                }
                
                .table-row:hover {
                    background: #f9fafb;
                }
                
                .table-row:last-child {
                    border-bottom: none;
                }
                
                .table-cell {
                    display: flex;
                    align-items: center;
                    font-size: 0.875rem;
                }
                
                .job-id {
                    font-family: 'SF Mono', Monaco, monospace;
                    background: #f3f4f6;
                    padding: 0.25rem 0.5rem;
                    border-radius: 0.375rem;
                    color: #374151;
                    font-weight: 600;
                }
                
                .sync-badge {
                    padding: 0.25rem 0.75rem;
                    border-radius: 9999px;
                    font-size: 0.75rem;
                    font-weight: 600;
                    text-transform: capitalize;
                }
                
                .sync-warning {
                    background: #fef3c7;
                    color: #92400e;
                }
                
                .sync-info {
                    background: #dbeafe;
                    color: #1e40af;
                }
                
                .sync-success {
                    background: #dcfce7;
                    color: #166534;
                }
                
                .sync-danger {
                    background: #fecaca;
                    color: #991b1b;
                }
                
                .sync-secondary {
                    background: #f3f4f6;
                    color: #374151;
                }
                
                .job-time, .job-records, .job-duration {
                    color: #6b7280;
                }
                
                .modal-overlay {
                    position: fixed;
                    top: 0;
                    left: 0;
                    width: 100%;
                    height: 100%;
                    background: rgba(0, 0, 0, 0.5);
                    display: flex;
                    justify-content: center;
                    align-items: center;
                    z-index: 1000;
                    backdrop-filter: blur(4px);
                }
                
                .modal-container {
                    background: white;
                    border-radius: 1rem;
                    width: 90%;
                    max-width: 600px;
                    max-height: 90vh;
                    overflow: hidden;
                    box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
                    display: flex;
                    flex-direction: column;
                }
                
                .modal-large {
                    max-width: 800px;
                }
                
                .modal-header {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    padding: 1.5rem;
                    border-bottom: 1px solid #e5e7eb;
                    background: #f9fafb;
                }
                
                .modal-header h2 {
                    font-size: 1.5rem;
                    font-weight: 600;
                    color: #111827;
                    margin: 0;
                }
                
                .modal-close {
                    background: none;
                    border: none;
                    font-size: 1.5rem;
                    color: #6b7280;
                    cursor: pointer;
                    padding: 0.5rem;
                    border-radius: 0.375rem;
                    transition: all 0.2s ease;
                }
                
                .modal-close:hover {
                    background: #f3f4f6;
                    color: #374151;
                }
                
                .modal-body {
                    flex: 1;
                    overflow-y: auto;
                    padding: 1.5rem;
                }
                
                .modal-footer {
                    display: flex;
                    justify-content: flex-end;
                    gap: 1rem;
                    padding: 1.5rem;
                    border-top: 1px solid #e5e7eb;
                    background: #f9fafb;
                }
                
                .form-section {
                    margin-bottom: 2rem;
                }
                
                .form-section h3 {
                    font-size: 1.125rem;
                    font-weight: 600;
                    color: #111827;
                    margin-bottom: 1rem;
                    padding-bottom: 0.5rem;
                    border-bottom: 1px solid #e5e7eb;
                }
                
                .form-group {
                    margin-bottom: 1rem;
                }
                
                .form-row {
                    display: grid;
                    grid-template-columns: 1fr 1fr;
                    gap: 1rem;
                }
                
                .form-group label {
                    display: block;
                    font-weight: 500;
                    color: #374151;
                    margin-bottom: 0.5rem;
                }
                
                .form-control {
                    width: 100%;
                    padding: 0.75rem;
                    border: 1px solid #d1d5db;
                    border-radius: 0.5rem;
                    font-size: 0.875rem;
                    transition: all 0.2s ease;
                    box-sizing: border-box;
                }
                
                .form-control:focus {
                    outline: none;
                    border-color: #3b82f6;
                    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
                }
                
                .checkbox-label {
                    display: flex !important;
                    align-items: center;
                    gap: 0.5rem;
                    cursor: pointer;
                    margin-bottom: 0 !important;
                }
                
                .checkbox-label input[type="checkbox"] {
                    margin: 0;
                }
                
                .checkmark {
                    font-weight: 500;
                }
                
                .error-message {
                    color: #dc2626;
                    font-size: 0.875rem;
                    margin-top: 0.25rem;
                    display: block;
                }
                
                .sync-settings-config {
                    margin-bottom: 1rem;
                }
                
                .config-header {
                    display: flex;
                    align-items: center;
                    gap: 0.5rem;
                    margin-bottom: 1rem;
                }
                
                .config-header h3 {
                    margin: 0;
                    border: none;
                    padding: 0;
                }
                
                .info-tooltip {
                    position: relative;
                    cursor: help;
                }
                
                .info-tooltip .fas {
                    color: #6b7280;
                    font-size: 1rem;
                }
                
                .tooltip-text {
                    visibility: hidden;
                    position: absolute;
                    bottom: 125%;
                    left: 50%;
                    transform: translateX(-50%);
                    background: #111827;
                    color: white;
                    padding: 0.5rem;
                    border-radius: 0.375rem;
                    font-size: 0.75rem;
                    white-space: nowrap;
                    z-index: 1001;
                    opacity: 0;
                    transition: opacity 0.2s ease;
                }
                
                .info-tooltip:hover .tooltip-text {
                    visibility: visible;
                    opacity: 1;
                }
                
                .sync-config-panel {
                    background: #f9fafb;
                    padding: 1rem;
                    border-radius: 0.5rem;
                    border: 1px solid #e5e7eb;
                    margin-top: 1rem;
                }
                
                .alert {
                    display: flex;
                    align-items: center;
                    gap: 0.75rem;
                    padding: 1rem;
                    border-radius: 0.5rem;
                    margin-bottom: 1rem;
                }
                
                .alert-success {
                    background: #ecfdf5;
                    border: 1px solid #a7f3d0;
                    color: #065f46;
                }
                
                .alert-danger {
                    background: #fef2f2;
                    border: 1px solid #fecaca;
                    color: #991b1b;
                }
                
                .code-block {
                    background: #f3f4f6;
                    padding: 1rem;
                    border-radius: 0.5rem;
                    border: 1px solid #e5e7eb;
                    font-family: 'SF Mono', Monaco, monospace;
                    font-size: 0.875rem;
                    overflow-x: auto;
                    white-space: pre-wrap;
                    word-break: break-all;
                }
                
                .sync-metrics {
                    margin: 1rem 0;
                }
                
                .metrics-grid {
                    display: grid;
                    grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
                    gap: 1rem;
                    background: #f9fafb;
                    padding: 1rem;
                    border-radius: 0.5rem;
                    border: 1px solid #e5e7eb;
                }
                
                .metric-item {
                    text-align: center;
                }
                
                .metric-label {
                    display: block;
                    font-size: 0.75rem;
                    color: #6b7280;
                    text-transform: uppercase;
                    letter-spacing: 0.05em;
                    margin-bottom: 0.25rem;
                }
                
                .metric-value {
                    display: block;
                    font-size: 1.5rem;
                    font-weight: 700;
                    color: #111827;
                }
                
                .metric-value.success {
                    color: #059669;
                }
                
                .metric-value.error {
                    color: #dc2626;
                }
                
                .sync-errors {
                    margin-top: 1rem;
                }
                
                .sync-errors h4 {
                    font-size: 1rem;
                    font-weight: 600;
                    color: #991b1b;
                    margin-bottom: 0.75rem;
                }
                
                .errors-list {
                    background: #fef2f2;
                    border: 1px solid #fecaca;
                    border-radius: 0.5rem;
                    padding: 1rem;
                }
                
                .error-item {
                    display: flex;
                    align-items: flex-start;
                    gap: 0.5rem;
                    margin-bottom: 0.5rem;
                    font-size: 0.875rem;
                    color: #991b1b;
                }
                
                .error-item:last-child {
                    margin-bottom: 0;
                }
                
                .error-item i {
                    margin-top: 0.125rem;
                    flex-shrink: 0;
                }
                
                .loading-container {
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    justify-content: center;
                    padding: 3rem 2rem;
                    text-align: center;
                }
                
                .loading-spinner {
                    width: 3rem;
                    height: 3rem;
                    border: 3px solid #f3f4f6;
                    border-top: 3px solid #3b82f6;
                    border-radius: 50%;
                    animation: spin 1s linear infinite;
                    margin-bottom: 1rem;
                }
                
                @keyframes spin {
                    0% { transform: rotate(0deg); }
                    100% { transform: rotate(360deg); }
                }
                
                .loading-container p {
                    color: #6b7280;
                    margin: 0;
                }
                
                .btn {
                    display: inline-flex;
                    align-items: center;
                    gap: 0.5rem;
                    padding: 0.5rem 1rem;
                    border-radius: 0.5rem;
                    font-weight: 500;
                    cursor: pointer;
                    transition: all 0.2s ease;
                    border: 1px solid transparent;
                    text-decoration: none;
                    justify-content: center;
                }
                
                .btn-primary {
                    background: linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%);
                    color: white;
                    border-color: #3b82f6;
                }
                
                .btn-primary:hover {
                    background: linear-gradient(135deg, #1d4ed8 0%, #1e40af 100%);
                    transform: translateY(-1px);
                    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
                }
                
                .btn-secondary {
                    background: white;
                    color: #374151;
                    border-color: #d1d5db;
                }
                
                .btn-secondary:hover {
                    background: #f9fafb;
                    border-color: #9ca3af;
                }
                
                /* Responsive Design */
                @media (max-width: 768px) {
                    .page-header {
                        flex-direction: column;
                        gap: 1rem;
                        align-items: flex-start;
                    }
                    
                    .integrations-grid {
                        grid-template-columns: 1fr;
                    }
                    
                    .stats-grid {
                        grid-template-columns: 1fr;
                    }
                    
                    .table-header,
                    .table-row {
                        grid-template-columns: 1fr;
                        gap: 0.5rem;
                    }
                    
                    .table-cell {
                        justify-content: space-between;
                        padding: 0.5rem 0;
                        border-bottom: 1px solid #f3f4f6;
                    }
                    
                    .table-cell:last-child {
                        border-bottom: none;
                    }
                    
                    .card-actions {
                        flex-wrap: wrap;
                    }
                    
                    .action-btn {
                        flex: 1;
                        min-width: 2.5rem;
                    }
                    
                    .modal-container {
                        width: 95%;
                        max-height: 95vh;
                    }
                    
                    .form-row {
                        grid-template-columns: 1fr;
                    }
                    
                    .metrics-grid {
                        grid-template-columns: repeat(2, 1fr);
                    }
                }
                
                @media (max-width: 480px) {
                    .page-header {
                        padding: 0 1rem;
                    }
                    
                    .content-container {
                        padding: 0 1rem;
                    }
                    
                    .breadcrumb {
                        margin: 0 1rem 2rem 1rem;
                    }
                    
                    .tab-navigation {
                        margin: 0 1rem 2rem 1rem;
                    }
                    
                    .tab-button {
                        padding: 0.5rem 1rem;
                        font-size: 0.875rem;
                    }
                    
                    .metrics-grid {
                        grid-template-columns: 1fr;
                    }
                }
            `}</style>
        </div>
    );
};

export default UnifiedIntegrationManagement;